import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  TextField,
  MenuItem,
} from "@mui/material";

const API = process.env.REACT_APP_API_URL;

const BILL_URLS = {
  hostel: `${API}hostel/generate-next-month-invoice/`,
  library: `${API}library/generate-next-month-invoice/`,
};
const INVOICE_URLS = {
  hostel: `${API}hostel/my-invoices/`,
  library: `${API}library/my-invoices/`,
};
const PDF_URLS = {
  hostel: (id) => `${API}hostel/invoices/${id}/download-pdf/`,
  library: (id) => `${API}library/invoices/${id}/download-pdf/`,
};

const StudentBill = () => {
  const [selectedBillType, setSelectedBillType] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("hostel");
  const [hostelInvoices, setHostelInvoices] = useState([]);
  const [libraryInvoices, setLibraryInvoices] = useState([]);
  const [filters, setFilters] = useState({ is_paid: "", month: "", year: "" });
  const [invoiceModal, setInvoiceModal] = useState({
    open: false,
    invoice: null,
  });

  // Fetch invoices
  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      // Build query params
      const params = new URLSearchParams();
      if (filters.is_paid) params.append("is_paid", filters.is_paid);
      if (filters.month) params.append("month", filters.month);
      if (filters.year) params.append("year", filters.year);
      // Hostel
      const hostelRes = await axios.get(
        `${INVOICE_URLS.hostel}?${params.toString()}`,
        config
      );
      setHostelInvoices(
        [...hostelRes.data].sort((a, b) => {
          // Prefer generated_on if available, else fallback to id
          if (a.generated_on && b.generated_on) {
            return new Date(b.generated_on) - new Date(a.generated_on);
          }
          return (b.id || 0) - (a.id || 0);
        })
      );
      // Library
      const libraryRes = await axios.get(
        `${INVOICE_URLS.library}?${params.toString()}`,
        config
      );
      setLibraryInvoices(
        [...libraryRes.data].sort((a, b) => {
          if (a.generated_on && b.generated_on) {
            return new Date(b.generated_on) - new Date(a.generated_on);
          }
          return (b.id || 0) - (a.id || 0);
        })
      );
    } catch (err) {
      toast.error("Failed to fetch invoices");
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line
  }, [filters]);

  // Generate invoice
  const handleGenerateBill = async () => {
    if (!selectedBillType) {
      toast.error("Please select a bill type");
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(
        BILL_URLS[selectedBillType],
        {
          student_id: localStorage.getItem("student_id"),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(
        `${
          selectedBillType === "hostel" ? "Hostel" : "Library"
        } bill generated 🎉`
      );
      setInvoiceModal({ open: true, invoice: res.data });
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.error || "Error generating bill");
    } finally {
      setLoading(false);
    }
  };

  // Download PDF
  const handleDownloadPDF = async (type, invoiceId) => {
    const loadingToast = toast.loading("Downloading invoice...");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(PDF_URLS[type](invoiceId), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${type}_invoice_${invoiceId}.pdf`;
      link.click();
      toast.dismiss(loadingToast);
      toast.success("✅ Invoice downloaded successfully.");
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to download invoice.");
    }
  };

  // Filter helpers
  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - 2 + i
  );
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-full mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl">
              <ReceiptOutlinedIcon className="text-white text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Generate Monthly Bill
              </h2>
              <p className="text-gray-600 text-sm">
                Create invoices for hostel and library services
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <select
                value={selectedBillType}
                onChange={(e) => setSelectedBillType(e.target.value)}
                // disabled={!isEligibleToGenerate()}
                // className={`w-full sm:w-auto px-6 py-3 rounded-xl font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                //   isEligibleToGenerate()
                //     ? "bg-yellow-400 text-black hover:bg-yellow-300 border-2 border-yellow-400"
                //     : "bg-gray-300 text-gray-600 cursor-not-allowed border-2 border-gray-300"
                // }`}
                className={
                  "w-full sm:w-auto px-6 py-3 rounded-xl font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-yellow-400 text-black hover:bg-yellow-300 border-2 border-yellow-400"
                }
              >
                <option value="">🧾 Generate Bill</option>
                <option value="hostel">🏨 Hostel Bed</option>
                <option value="library">📚 Library Seat</option>
              </select>

              <Button
                onClick={handleGenerateBill}
                // disabled={
                //   !selectedBillType || !isEligibleToGenerate() || loading
                // }
                variant="contained"
                className={`w-full sm:w-auto px-6 py-3 rounded-xl font-semibold transition-colors duration-200 bg-blue-600 text-white hover:bg-blue-700 
                  `}
                // ${
                //     // !selectedBillType || !isEligibleToGenerate()
                //     //   ? "bg-gray-400 text-white cursor-not-allowed"
                //     //   : "bg-blue-600 text-white hover:bg-blue-700"
                //   }`
                // }
                sx={{ borderRadius: "12px" }}
              >
                {loading ? "Generating..." : "Generate"}
              </Button>
            </div>

            {/* {!isEligibleToGenerate() && ( */}
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-sm">
                ⚠️ Bill can only be generated in the last 3 days of current
                cycle.
              </p>
            </div>
            {/* )} */}
          </div>
        </div>

        {/* Invoice Tabs and Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
          <div className="flex items-center justify-between mb-2">
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Hostel Invoices" value="hostel" />
              <Tab label="Library Invoices" value="library" />
            </Tabs>
            <button
              onClick={fetchInvoices}
              className="flex items-center gap-2 bg-gray-100 text-gray-600 p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ml-2"
              title="Refresh invoices"
            >
              <RefreshIcon
                className={`text-sm ${loading ? "animate-spin" : ""}`}
              />
            </button>
          </div>
          <Box className="flex flex-wrap gap-3 mt-4 mb-4">
            <TextField
              select
              label="Paid Status"
              value={filters.is_paid}
              onChange={(e) =>
                setFilters((f) => ({ ...f, is_paid: e.target.value }))
              }
              size="small"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Paid</MenuItem>
              <MenuItem value="false">Unpaid</MenuItem>
            </TextField>
            <TextField
              select
              label="Month"
              value={filters.month}
              onChange={(e) =>
                setFilters((f) => ({ ...f, month: e.target.value }))
              }
              size="small"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              {monthNames.map((name, idx) => (
                <MenuItem
                  key={name}
                  value={(idx + 1).toString().padStart(2, "0")}
                >
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Year"
              value={filters.year}
              onChange={(e) =>
                setFilters((f) => ({ ...f, year: e.target.value }))
              }
              size="small"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Invoice Card/Table List */}
          {/* Table for md+ screens */}
          <Box className="hidden md:block overflow-x-auto mt-4">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Invoice ID
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Month
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Amount
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Deposit
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Total
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Status
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Generated On
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(tab === "hostel" ? hostelInvoices : libraryInvoices)
                  .length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <ReceiptOutlinedIcon className="text-gray-400 text-2xl" />
                        </div>
                        <p className="text-gray-500 font-medium">
                          No invoices found
                        </p>
                        <p className="text-gray-400 text-sm">
                          Try adjusting your filters or refresh the page
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  (tab === "hostel" ? hostelInvoices : libraryInvoices).map(
                    (inv, idx) => (
                      <tr
                        key={idx}
                        className="hover:bg-gray-50 transition-colors duration-200"
                      >
                        <td className="px-4 py-4 font-mono text-gray-600">
                          {inv.invoice_id}
                        </td>
                        <td className="px-4 py-4 text-gray-600">{inv.month}</td>
                        <td className="px-4 py-4 text-gray-600">
                          ₹{inv.amount}
                        </td>
                        <td className="px-4 py-4 text-gray-600">
                          ₹{inv.deposit}
                        </td>
                        <td className="px-4 py-4 font-bold text-gray-800">
                          ₹{inv.total}
                        </td>
                        <td className="px-4 py-4">
                          {inv.is_paid ? (
                            <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                              Paid
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                              Unpaid
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-gray-600 max-w-xs truncate">
                          {new Date(inv.generated_on).toLocaleString()}
                        </td>
                        <td className="px-4 py-4">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleDownloadPDF(tab, inv.id)}
                            className="rounded-lg border-blue-500 text-blue-600 hover:bg-blue-50"
                            startIcon={<ReceiptOutlinedIcon />}
                          >
                            Download PDF
                          </Button>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </Box>
          {/* Cards for mobile screens */}
          <Box className="grid grid-cols-1 gap-6 mt-4 md:hidden">
            {(tab === "hostel" ? hostelInvoices : libraryInvoices).length ===
            0 ? (
              <div className="col-span-full text-center py-6 text-gray-500 bg-gray-50 rounded-xl shadow-inner">
                No invoices found.
              </div>
            ) : (
              (tab === "hostel" ? hostelInvoices : libraryInvoices).map(
                (inv, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-xl shadow-md p-5 flex flex-col gap-3 border border-gray-100 hover:shadow-lg transition-shadow duration-200"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <ReceiptOutlinedIcon className="text-yellow-500 text-2xl" />
                      <div>
                        <div className="font-bold text-lg text-gray-800">
                          {inv.invoice_id}
                        </div>
                        <div className="text-xs text-gray-500">
                          {inv.organisation}
                        </div>
                      </div>
                      <div className="ml-auto">
                        {inv.is_paid ? (
                          <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                            Paid
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                            Unpaid
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-700">
                      <div className="flex-1 min-w-[120px]">
                        <span className="font-medium">Month:</span> {inv.month}
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <span className="font-medium">Amount:</span> ₹
                        {inv.amount}
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <span className="font-medium">Deposit:</span> ₹
                        {inv.deposit}
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <span className="font-medium">Total:</span>{" "}
                        <span className="font-bold">₹{inv.total}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                      <div>
                        Generated: {new Date(inv.generated_on).toLocaleString()}
                      </div>
                      {inv.room && <div>Room: {inv.room}</div>}
                      {inv.bed && <div>Bed: {inv.bed}</div>}
                      {inv.seat && <div>Seat: {inv.seat}</div>}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDownloadPDF(tab, inv.id)}
                        className="rounded-lg border-blue-500 text-blue-600 hover:bg-blue-50"
                        startIcon={<ReceiptOutlinedIcon />}
                      >
                        Download PDF
                      </Button>
                    </div>
                  </div>
                )
              )
            )}
          </Box>
        </div>
      </div>

      {/* Invoice Details Modal */}
      <Dialog
        open={invoiceModal.open}
        onClose={() => setInvoiceModal({ open: false, invoice: null })}
      >
        <DialogTitle>Invoice Generated</DialogTitle>
        <DialogContent>
          {invoiceModal.invoice && (
            <Box className="space-y-2 p-2 md:p-4">
              <div className="flex items-center gap-3 mb-2">
                <ReceiptOutlinedIcon className="text-yellow-500 text-2xl" />
                <div>
                  <div className="font-bold text-lg text-gray-800">
                    {invoiceModal.invoice.invoice_id}
                  </div>
                  <div className="text-xs text-gray-500">
                    {invoiceModal.invoice.organisation}
                  </div>
                </div>
                <div className="ml-auto">
                  {invoiceModal.invoice.is_paid ? (
                    <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                      Paid
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                      Unpaid
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-sm text-gray-700">
                <div className="flex-1 min-w-[120px]">
                  <span className="font-medium">Month:</span>{" "}
                  {invoiceModal.invoice.month}
                </div>
                <div className="flex-1 min-w-[120px]">
                  <span className="font-medium">Amount:</span> ₹
                  {invoiceModal.invoice.amount}
                </div>
                <div className="flex-1 min-w-[120px]">
                  <span className="font-medium">Deposit:</span> ₹
                  {invoiceModal.invoice.deposit}
                </div>
                <div className="flex-1 min-w-[120px]">
                  <span className="font-medium">Total:</span>{" "}
                  <span className="font-bold">
                    ₹{invoiceModal.invoice.total}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                <div>
                  Generated:{" "}
                  {new Date(invoiceModal.invoice.generated_on).toLocaleString()}
                </div>
                {invoiceModal.invoice.room && (
                  <div>Room: {invoiceModal.invoice.room}</div>
                )}
                {invoiceModal.invoice.bed && (
                  <div>Bed: {invoiceModal.invoice.bed}</div>
                )}
                {invoiceModal.invoice.seat && (
                  <div>Seat: {invoiceModal.invoice.seat}</div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    handleDownloadPDF(
                      invoiceModal.invoice.booking_type,
                      invoiceModal.invoice.id
                    )
                  }
                  className="rounded-lg border-blue-500 text-blue-600 hover:bg-blue-50"
                  startIcon={<ReceiptOutlinedIcon />}
                >
                  Download PDF
                </Button>
              </div>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInvoiceModal({ open: false, invoice: null })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default StudentBill;
